import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Mail, ArrowLeft, CheckCircle, AlertCircle } from "lucide-react";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess(false);
    setIsLoading(true);

    try {
      const response = await axios.post("https://api-ec.cinef-expertises.com/api/clients/forgot-password", {
        email,
      });

      setSuccess(true);
      setEmail("");
    } catch (err) {
      console.error(err.response?.data);
      setError(
        err.response?.data?.message || 
        "Une erreur s'est produite. Veuillez vérifier votre adresse email."
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="forgot-password-page">
      <div className="forgot-password-container">
        <div className="icon-container">
          <div className="mail-icon">
            <Mail size={48} strokeWidth={1.5} />
          </div>
        </div>

        {!success ? (
          <>
            <div className="header-text">
              <h2>Mot de passe oublié ?</h2>
              <p>Entrez votre adresse email et nous vous enverrons un lien pour réinitialiser votre mot de passe</p>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Adresse email</label>
                <input
                  type="email"
                  className="form-control"
                  placeholder="exemple@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={isLoading}
                />
              </div>

              {error && (
                <div className="error-message">
                  <AlertCircle size={18} />
                  {error}
                </div>
              )}

              <button type="submit" className="btn-submit" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <span className="spinner"></span>
                    Envoi en cours...
                  </>
                ) : (
                  "Envoyer le lien de réinitialisation"
                )}
              </button>
            </form>

            <div className="back-to-login">
              <Link to="/loginuser" className="back-link">
                <ArrowLeft size={16} />
                Retour à la connexion
              </Link>
            </div>
          </>
        ) : (
          <div className="success-container">
            <div className="success-icon">
              <CheckCircle size={64} strokeWidth={1.5} />
            </div>
            <h2>Email envoyé !</h2>
            <p className="success-text">
              Un email de réinitialisation a été envoyé à votre adresse email. 
              Veuillez vérifier votre boîte de réception et suivre les instructions.
            </p>
            <p className="help-text">
              Vous n'avez pas reçu l'email ? Vérifiez vos spams ou réessayez dans quelques minutes.
            </p>
            <Link to="/loginuser" className="btn-back">
              <ArrowLeft size={18} />
              Retour à la connexion
            </Link>
          </div>
        )}
      </div>

      <style>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        .forgot-password-page {
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: 100vh;
          background: linear-gradient(135deg, #f5f7fa 0%, #e8eef3 100%);
          padding: 40px 20px;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
        }

        .forgot-password-container {
          width: 100%;
          max-width: 440px;
          background: white;
          padding: 48px 40px;
          border-radius: 16px;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
        }

        .icon-container {
          display: flex;
          justify-content: center;
          margin-bottom: 32px;
        }

        .mail-icon {
          width: 80px;
          height: 80px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #008A44 0%, #006633 100%);
          color: white;
          border-radius: 20px;
          box-shadow: 0 8px 24px rgba(0, 138, 68, 0.2);
        }

        .header-text {
          text-align: center;
          margin-bottom: 32px;
        }

        .header-text h2 {
          color: #1a1a1a;
          font-size: 28px;
          font-weight: 700;
          margin-bottom: 12px;
        }

        .header-text p {
          color: #6b7280;
          font-size: 15px;
          line-height: 1.6;
          margin: 0;
        }

        .form-group {
          margin-bottom: 24px;
        }

        .form-group label {
          display: block;
          font-weight: 600;
          font-size: 14px;
          color: #374151;
          margin-bottom: 8px;
        }

        .form-control {
          width: 100%;
          padding: 12px 16px;
          font-size: 15px;
          border: 2px solid #e5e7eb;
          border-radius: 8px;
          transition: all 0.2s ease;
          background: #fafafa;
        }

        .form-control:focus {
          outline: none;
          border-color: #008A44;
          background: white;
          box-shadow: 0 0 0 3px rgba(0, 138, 68, 0.1);
        }

        .form-control:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .form-control::placeholder {
          color: #9ca3af;
        }

        .error-message {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 16px;
          background: #fef2f2;
          border: 1px solid #fecaca;
          border-radius: 8px;
          color: #dc2626;
          font-size: 14px;
          margin-bottom: 20px;
        }

        .btn-submit {
          width: 100%;
          padding: 14px;
          font-size: 16px;
          font-weight: 600;
          background: linear-gradient(135deg, #008A44 0%, #006633 100%);
          color: white;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 4px 12px rgba(0, 138, 68, 0.2);
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }

        .btn-submit:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(0, 138, 68, 0.3);
        }

        .btn-submit:active:not(:disabled) {
          transform: translateY(0);
        }

        .btn-submit:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .spinner {
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 0.6s linear infinite;
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }

        .back-to-login {
          margin-top: 24px;
          text-align: center;
        }

        .back-link {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          color: #6b7280;
          text-decoration: none;
          font-size: 14px;
          font-weight: 500;
          transition: color 0.2s ease;
        }

        .back-link:hover {
          color: #008A44;
        }

        .back-link:hover svg {
          transform: translateX(-4px);
        }

        /* Success Container */
        .success-container {
          text-align: center;
        }

        .success-icon {
          display: flex;
          justify-content: center;
          margin-bottom: 24px;
          color: #16a34a;
        }

        .success-container h2 {
          color: #1a1a1a;
          font-size: 28px;
          font-weight: 700;
          margin-bottom: 16px;
        }

        .success-text {
          color: #374151;
          font-size: 15px;
          line-height: 1.6;
          margin-bottom: 16px;
        }

        .help-text {
          color: #6b7280;
          font-size: 14px;
          line-height: 1.5;
          margin-bottom: 32px;
          padding: 12px;
          background: #f9fafb;
          border-radius: 8px;
        }

        .btn-back {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          width: 100%;
          padding: 14px;
          font-size: 16px;
          font-weight: 600;
          background: linear-gradient(135deg, #008A44 0%, #006633 100%);
          color: white;
          border: none;
          border-radius: 8px;
          text-decoration: none;
          transition: all 0.3s ease;
          box-shadow: 0 4px 12px rgba(0, 138, 68, 0.2);
        }

        .btn-back:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(0, 138, 68, 0.3);
          color: white;
        }

        .btn-back:active {
          transform: translateY(0);
        }

        @media (max-width: 500px) {
          .forgot-password-container {
            padding: 36px 28px;
          }

          .header-text h2,
          .success-container h2 {
            font-size: 24px;
          }

          .header-text p,
          .success-text {
            font-size: 14px;
          }

          .mail-icon {
            width: 70px;
            height: 70px;
          }
        }
      `}</style>
    </div>
  );
};

export default ForgotPassword;